import React from 'react';
import { TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

const CASES = [
  {
    title: 'The Missing Break Clause Issue',
    problem: 'A landlord discovered their break clause didn\'t comply with common law requirements, potentially making it unenforceable.',
    solution: 'Our AI flagged the issue and provided a corrected version that meets all legal requirements.',
    impact: 'Avoided \u00A33,000+ in legal fees to fix the issue later',
    tag: 'Estimated savings',
  },
  {
    title: 'Renters Rights Bill Non-Compliance',
    problem: 'Multiple clauses in an outdated template violated the 2025 Renters Rights Bill.',
    solution: 'Complete rewrite ensuring full compliance with the latest legislation.',
    impact: 'Agreement now fully enforceable and up-to-date with law',
    tag: 'Full compliance',
  },
  {
    title: 'Rental Provisions Gap',
    problem: 'No clear guidance on rent increases, leaving tenant relationship ambiguous.',
    solution: 'Added compliant rent increase provisions with proper notice requirements.',
    impact: 'Clear expectations prevent disputes and enforcement challenges',
    tag: 'Dispute prevention',
  },
];

export function CaseStudies() {
  return (
    <section className="py-20 sm:py-28 bg-gray-50 bg-diagonal-lines">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Real-World Results</h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            How landlords and property managers protected themselves with our reviews
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {CASES.map((c, i) => (
            <div
              key={c.title}
              className={`animate-on-scroll delay-${(i + 1) * 100} bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-elevated transition-shadow duration-300`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-semibold text-gray-900 text-sm leading-snug pr-2">{c.title}</h3>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">{c.tag}</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">The Problem</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{c.problem}</p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">The Solution</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{c.solution}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-gray-900">{c.impact}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
